import { useMemo } from 'react'
import BigNumber from 'bignumber.js'
import { useSelector } from 'react-redux'
import { appDispatchAsyncThunk } from 'state'
import { getAccount } from 'state/web3/account'
import { getAllLoadedFarmTokenPrices } from 'state/farms/useFarmTokensPricesUsdc'
import { simpleRpcProvider } from 'utils/providers'
import createPeriodicRefresher from 'utils/createPeriodicRefresher'
import {
  fetchPoolsPublicDataAsync,
  fetchPoolsUserDataAsync,
  fetchVvsVaultPublicData,
  fetchVvsVaultUserData,
  fetchVvsVaultFees,
  fetchPoolsStakingLimitsAsync,
} from '.'
import { State, DeserializedPool } from '../types'
import { transformPool } from './helpers'

export const useFetchPublicPoolsData = createPeriodicRefresher(
  async () => {
    const [blockNumber, prices] = await Promise.all([simpleRpcProvider.getBlockNumber(), getAllLoadedFarmTokenPrices()])

    await Promise.all([
      appDispatchAsyncThunk(fetchPoolsPublicDataAsync(blockNumber, prices)),
      appDispatchAsyncThunk(fetchPoolsStakingLimitsAsync()),
    ])
  },
  {
    frequency: 'normal',
    dbgName: 'PollPublicPoolsData',
  },
)

export const useFetchUserPoolsRefresher = createPeriodicRefresher(
  async () => {
    const account = getAccount()
    if (!account) return

    await appDispatchAsyncThunk(fetchPoolsUserDataAsync(account))
  },
  {
    frequency: 'fast',
    dbgName: 'PollUserPools',
  },
)

export const useFetchPublicVvsVault = createPeriodicRefresher(
  async () => {
    await Promise.all([appDispatchAsyncThunk(fetchVvsVaultPublicData()), appDispatchAsyncThunk(fetchVvsVaultFees())])
  },
  {
    frequency: 'normal',
    dbgName: 'PollPublicVvsVault',
  },
)

export const useFetchUserVvsVault = createPeriodicRefresher(
  async () => {
    const account = getAccount()
    if (!account) return

    appDispatchAsyncThunk(fetchVvsVaultUserData({ account }))
  },
  {
    frequency: 'fast',
    dbgName: 'PollUserVvsVault',
  },
)

export const useFetchUserPools = (account: string) => {
  useFetchUserPoolsRefresher(Boolean(account))
}

export const useFetchVvsVault = (account: string) => {
  useFetchPublicVvsVault()
  useFetchUserVvsVault(Boolean(account))
}

export const usePools = (): { pools: DeserializedPool[]; userDataLoaded: boolean } => {
  const { pools, userDataLoaded } = useSelector((state: State) => ({
    pools: state.pools.data,
    userDataLoaded: state.pools.userDataLoaded,
  }))
  return { pools: pools.map(transformPool), userDataLoaded }
}

export const useVvsVault = () => {
  const {
    totalShares: totalSharesAsString,
    pricePerFullShare: pricePerFullShareAsString,
    totalVvsInVault: totalVvsInVaultAsString,
    estimatedVvsBountyReward: estimatedVvsBountyRewardAsString,
    totalPendingVvsHarvest: totalPendingVvsHarvestAsString,
    fees: { performanceFee, callFee, withdrawalFee, withdrawalFeePeriod },
    userData: {
      isLoading,
      userShares: userSharesAsString,
      vvsAtLastUserAction: vvsAtLastUserActionAsString,
      lastDepositedTime,
      lastUserActionTime,
    },
  } = useSelector((state: State) => state.pools.vvsVault)

  const estimatedVvsBountyReward = useMemo(() => {
    return new BigNumber(estimatedVvsBountyRewardAsString)
  }, [estimatedVvsBountyRewardAsString])

  const totalPendingVvsHarvest = useMemo(() => {
    return new BigNumber(totalPendingVvsHarvestAsString)
  }, [totalPendingVvsHarvestAsString])

  const totalShares = useMemo(() => {
    return new BigNumber(totalSharesAsString)
  }, [totalSharesAsString])

  const pricePerFullShare = useMemo(() => {
    return new BigNumber(pricePerFullShareAsString)
  }, [pricePerFullShareAsString])

  const totalVvsInVault = useMemo(() => {
    return new BigNumber(totalVvsInVaultAsString)
  }, [totalVvsInVaultAsString])

  const userShares = useMemo(() => {
    return new BigNumber(userSharesAsString)
  }, [userSharesAsString])

  const vvsAtLastUserAction = useMemo(() => {
    return new BigNumber(vvsAtLastUserActionAsString)
  }, [vvsAtLastUserActionAsString])

  return {
    totalShares,
    pricePerFullShare,
    totalVvsInVault,
    estimatedVvsBountyReward,
    totalPendingVvsHarvest,
    fees: {
      performanceFee,
      callFee,
      withdrawalFee,
      withdrawalFeePeriod,
    },
    userData: {
      isLoading,
      userShares,
      vvsAtLastUserAction,
      lastDepositedTime,
      lastUserActionTime,
    },
  }
}
